// Timeline Component v1.0.0 - UniFi Protect-style scrubber
import { useRef, useEffect, useState, useCallback, useMemo } from 'react'
import { format, eachHourOfInterval } from 'date-fns'
import { useViewerStore } from '../stores/viewerStore'
import type { TimelineMarker } from '../types'

interface TimelineProps {
  onTimeChange?: (time: Date) => void
}

export default function Timeline({ onTimeChange }: TimelineProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [hoverTime, setHoverTime] = useState<Date | null>(null)
  const [hoverX, setHoverX] = useState(0)

  const {
    currentTime,
    timelineStart,
    timelineEnd,
    segments,
    selectedChannels,
    zoomLevel,
    setCurrentTime,
    zoomIn,
    zoomOut,
    panLeft,
    panRight,
    isPlaying,
    pause,
  } = useViewerStore()

  // Calculate timeline metrics
  const timelineRange = useMemo(() => {
    return timelineEnd.getTime() - timelineStart.getTime()
  }, [timelineStart, timelineEnd])

  const hours = useMemo(() => {
    return eachHourOfInterval({ start: timelineStart, end: timelineEnd })
  }, [timelineStart, timelineEnd])

  // Convert time to X position (0-1)
  const timeToX = useCallback(
    (time: Date): number => {
      return (time.getTime() - timelineStart.getTime()) / timelineRange
    },
    [timelineStart, timelineRange]
  )

  // Convert X position (0-1) to time
  const xToTime = useCallback(
    (x: number): Date => {
      const ms = timelineStart.getTime() + x * timelineRange
      return new Date(ms)
    },
    [timelineStart, timelineRange]
  )

  // Get X from mouse event
  const getMouseX = useCallback(
    (e: React.MouseEvent | MouseEvent): number => {
      if (!containerRef.current) return 0
      const rect = containerRef.current.getBoundingClientRect()
      return Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    },
    []
  )

  // Handle mouse events for scrubbing
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault()
      setIsDragging(true)
      if (isPlaying) pause()
      const x = getMouseX(e)
      const time = xToTime(x)
      setCurrentTime(time)
      onTimeChange?.(time)
    },
    [getMouseX, xToTime, setCurrentTime, onTimeChange, isPlaying, pause]
  )

  const handleMouseMove = useCallback(
    (e: React.MouseEvent | MouseEvent) => {
      const x = getMouseX(e)
      setHoverX(x)
      setHoverTime(xToTime(x))

      if (isDragging) {
        const time = xToTime(x)
        setCurrentTime(time)
        onTimeChange?.(time)
      }
    },
    [isDragging, getMouseX, xToTime, setCurrentTime, onTimeChange]
  )

  const handleMouseUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  const handleMouseLeave = useCallback(() => {
    setHoverTime(null)
    if (isDragging) {
      setIsDragging(false)
    }
  }, [isDragging])

  // Global mouse events for dragging outside container
  useEffect(() => {
    if (isDragging) {
      const handleGlobalMove = (e: MouseEvent) => handleMouseMove(e)
      const handleGlobalUp = () => handleMouseUp()
      window.addEventListener('mousemove', handleGlobalMove)
      window.addEventListener('mouseup', handleGlobalUp)
      return () => {
        window.removeEventListener('mousemove', handleGlobalMove)
        window.removeEventListener('mouseup', handleGlobalUp)
      }
    }
  }, [isDragging, handleMouseMove, handleMouseUp])

  // Wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault()
      if (e.deltaY < 0) {
        zoomIn()
      } else {
        zoomOut()
      }
    },
    [zoomIn, zoomOut]
  )

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.target instanceof HTMLInputElement) return
      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault()
          if (e.shiftKey) panLeft()
          else {
            const newTime = new Date(currentTime.getTime() - 10000) // 10 seconds
            setCurrentTime(newTime)
            onTimeChange?.(newTime)
          }
          break
        case 'ArrowRight':
          e.preventDefault()
          if (e.shiftKey) panRight()
          else {
            const newTime = new Date(currentTime.getTime() + 10000) // 10 seconds
            setCurrentTime(newTime)
            onTimeChange?.(newTime)
          }
          break
        case '+':
        case '=':
          e.preventDefault()
          zoomIn()
          break
        case '-':
          e.preventDefault()
          zoomOut()
          break
      }
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [currentTime, setCurrentTime, onTimeChange, panLeft, panRight, zoomIn, zoomOut])

  // Generate markers from segments
  const markers = useMemo((): TimelineMarker[] => {
    const result: TimelineMarker[] = []
    for (const segment of segments) {
      // Add markers for clips with threats
      if (segment.hasThreat) {
        result.push({
          time: segment.startTime,
          type: 'threat',
          severity: 'danger',
          label: 'Threat Detected',
        })
      }
      // Add markers for child content
      if (segment.hasChildContent) {
        result.push({
          time: segment.startTime,
          type: 'child',
          severity: 'warning',
          label: 'Protected Content',
        })
      }
    }
    return result
  }, [segments])

  // Current time indicator position
  const currentX = timeToX(currentTime) * 100

  return (
    <div className="w-full bg-viewer-surface border-t border-viewer-border">
      {/* Zoom level indicator */}
      <div className="flex items-center justify-between px-4 py-1 border-b border-viewer-border text-xs text-viewer-text-secondary">
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={zoomLevel <= 1}
            className="px-2 py-0.5 rounded bg-viewer-surface-hover hover:bg-viewer-border disabled:opacity-50"
          >
            -
          </button>
          <span className="w-20 text-center">
            {zoomLevel <= 1 ? '24h' : zoomLevel <= 2 ? '12h' : zoomLevel <= 4 ? '6h' : zoomLevel <= 8 ? '3h' : zoomLevel <= 16 ? '1.5h' : '45m'}
          </span>
          <button
            onClick={zoomIn}
            disabled={zoomLevel >= 64}
            className="px-2 py-0.5 rounded bg-viewer-surface-hover hover:bg-viewer-border disabled:opacity-50"
          >
            +
          </button>
        </div>
        <div className="flex items-center gap-4">
          <span>{format(timelineStart, 'MMM d, HH:mm')}</span>
          <span>-</span>
          <span>{format(timelineEnd, 'MMM d, HH:mm')}</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={panLeft} className="px-2 py-0.5 rounded bg-viewer-surface-hover hover:bg-viewer-border">
            ←
          </button>
          <button onClick={panRight} className="px-2 py-0.5 rounded bg-viewer-surface-hover hover:bg-viewer-border">
            →
          </button>
        </div>
      </div>

      {/* Timeline scrubber */}
      <div
        ref={containerRef}
        className="relative h-24 cursor-crosshair select-none"
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
        onWheel={handleWheel}
      >
        {/* Hour markers */}
        <div className="absolute inset-0 flex">
          {hours.map((hour) => {
            const x = timeToX(hour) * 100
            if (x < 0 || x > 100) return null
            return (
              <div
                key={hour.getTime()}
                className="absolute top-0 bottom-0 border-l border-viewer-border"
                style={{ left: `${x}%` }}
              >
                <span className="absolute top-0 left-1 text-xs text-viewer-text-muted">
                  {format(hour, 'HH:mm')}
                </span>
              </div>
            )
          })}
        </div>

        {/* Recording segments per channel */}
        <div className="absolute inset-x-0 top-5 bottom-8">
          {selectedChannels.map((channel, channelIdx) => {
            const channelSegments = segments.filter((s) => s.channels.includes(channel))
            const yOffset = (channelIdx / selectedChannels.length) * 100
            const height = 100 / selectedChannels.length - 2

            return (
              <div
                key={channel}
                className="absolute inset-x-0"
                style={{ top: `${yOffset}%`, height: `${height}%` }}
              >
                {/* Channel label */}
                <span className="absolute left-1 top-0 text-xs text-viewer-text-secondary z-10">
                  {channel.replace('_', ' ')}
                </span>

                {/* Segment bars */}
                {channelSegments.map((segment) => {
                  const startX = timeToX(segment.startTime) * 100
                  const endX = timeToX(segment.endTime) * 100
                  const width = endX - startX
                  if (startX > 100 || endX < 0) return null

                  const bgColor = segment.hasThreat
                    ? 'bg-viewer-danger/40'
                    : segment.hasChildContent
                    ? 'bg-detection-child/40'
                    : segment.eventType === 'SentryMode'
                    ? 'bg-viewer-accent/40'
                    : 'bg-viewer-success/40'

                  return (
                    <div
                      key={segment.id}
                      className={`absolute top-2 bottom-0 rounded-sm ${bgColor} border-l-2 ${
                        segment.hasThreat ? 'border-viewer-danger' : segment.hasChildContent ? 'border-detection-child' : 'border-viewer-accent'
                      }`}
                      style={{
                        left: `${Math.max(0, startX)}%`,
                        width: `${Math.min(width, 100 - startX)}%`,
                      }}
                    />
                  )
                })}
              </div>
            )
          })}
        </div>

        {/* Markers (threats, child content) */}
        {markers.map((marker, i) => {
          const x = timeToX(marker.time) * 100
          if (x < 0 || x > 100) return null
          return (
            <div
              key={`${marker.type}-${i}`}
              className={`absolute top-4 w-2 h-2 rounded-full transform -translate-x-1 ${
                marker.severity === 'danger'
                  ? 'bg-viewer-danger'
                  : marker.severity === 'warning'
                  ? 'bg-viewer-warning'
                  : 'bg-viewer-info'
              }`}
              style={{ left: `${x}%` }}
              title={marker.label}
            />
          )
        })}

        {/* Hover indicator */}
        {hoverTime && !isDragging && (
          <div
            className="absolute top-0 bottom-0 w-px bg-viewer-text-muted/50 pointer-events-none"
            style={{ left: `${hoverX * 100}%` }}
          >
            <div className="absolute -top-5 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded bg-viewer-surface-hover text-xs whitespace-nowrap">
              {format(hoverTime, 'HH:mm:ss')}
            </div>
          </div>
        )}

        {/* Current time indicator (playhead) */}
        <div
          className="absolute top-0 bottom-0 w-0.5 bg-viewer-accent pointer-events-none z-20"
          style={{ left: `${currentX}%` }}
        >
          {/* Playhead triangle */}
          <div className="absolute -top-1 left-1/2 transform -translate-x-1/2 w-0 h-0 border-l-[6px] border-l-transparent border-r-[6px] border-r-transparent border-t-[8px] border-t-viewer-accent" />
          {/* Time label */}
          <div className="absolute -bottom-5 left-1/2 transform -translate-x-1/2 px-2 py-0.5 rounded bg-viewer-accent text-xs text-white whitespace-nowrap font-mono">
            {format(currentTime, 'HH:mm:ss')}
          </div>
        </div>
      </div>

      {/* Timeline legend */}
      <div className="flex items-center justify-center gap-6 px-4 py-1 border-t border-viewer-border text-xs">
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-viewer-accent/40 border-l-2 border-viewer-accent" />
          <span className="text-viewer-text-secondary">Sentry</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-viewer-success/40 border-l-2 border-viewer-success" />
          <span className="text-viewer-text-secondary">Recording</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-viewer-danger/40 border-l-2 border-viewer-danger" />
          <span className="text-viewer-text-secondary">Threat</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="w-3 h-3 rounded-sm bg-detection-child/40 border-l-2 border-detection-child" />
          <span className="text-viewer-text-secondary">Protected</span>
        </div>
      </div>
    </div>
  )
}
